import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { useUserOptional } from "@/auth/UserContext";
import { resolveSurchargeDescription } from "@/lib/surchargeCodes";
import { api } from "@/services/api";

interface SurchargeCodesContextValue {
  /** Standard code → display description. */
  codeMap: Record<string, string>;
  /** Alias → standard code. */
  aliasToCode: Record<string, string>;
  /**
   * Mapped display description when the registry (or an alias) has one.
   * Falls back to `null` so callers can skip the hover.
   */
  describe: (code: string) => string | null;
  loading: boolean;
}

const SurchargeCodesContext = createContext<SurchargeCodesContextValue | null>(
  null,
);

export function SurchargeCodesProvider({ children }: { children: ReactNode }) {
  const user = useUserOptional();
  const isAuthenticated = user?.isAuthenticated ?? false;
  const isAuthLoading = user?.isAuthLoading ?? true;
  const clientKey = user?.profile?.client_key ?? null;

  const [codeMap, setCodeMap] = useState<Record<string, string>>({});
  const [aliasToCode, setAliasToCode] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isAuthLoading) return;
    if (!isAuthenticated) {
      setCodeMap({});
      setAliasToCode({});
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    Promise.allSettled([api.getSurchargeCodes(), api.getSurchargeCodeAliases()])
      .then(([codesResult, aliasesResult]) => {
        if (cancelled) return;
        if (codesResult.status === "fulfilled") {
          setCodeMap(codesResult.value);
        } else {
          console.warn("Failed to load surcharge codes:", codesResult.reason);
        }
        if (aliasesResult.status === "fulfilled") {
          const next: Record<string, string> = {};
          for (const row of aliasesResult.value) {
            const alias = row.alias?.trim().toUpperCase();
            const code = row.code?.trim().toUpperCase();
            if (alias && code) next[alias] = code;
          }
          setAliasToCode(next);
        } else {
          console.warn(
            "Failed to load surcharge code aliases:",
            aliasesResult.reason,
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, isAuthLoading, clientKey]);

  const describe = useCallback(
    (code: string): string | null =>
      resolveSurchargeDescription(code, codeMap, aliasToCode),
    [codeMap, aliasToCode],
  );

  const value = useMemo(
    (): SurchargeCodesContextValue => ({
      codeMap,
      aliasToCode,
      describe,
      loading,
    }),
    [codeMap, aliasToCode, describe, loading],
  );

  return (
    <SurchargeCodesContext.Provider value={value}>
      {children}
    </SurchargeCodesContext.Provider>
  );
}

export function useSurchargeCodes(): SurchargeCodesContextValue {
  const ctx = useContext(SurchargeCodesContext);
  if (!ctx) {
    throw new Error(
      "useSurchargeCodes must be used within SurchargeCodesProvider",
    );
  }
  return ctx;
}

export function useSurchargeCodesOptional(): SurchargeCodesContextValue | null {
  return useContext(SurchargeCodesContext);
}
